
















import { Vue, Component } from 'vue-property-decorator';

import segmentIo from '@/utils/segment.io';

import { setBusinessOnboardingDataOnLocalStorage } from '@/store/modules/businessOnboarding';

import BaseLayout from './Steps/BaseLayout.vue';
import BaseLayoutAI from './Steps/ai-signup/BaseLayout.vue';

import {
  isExternalOauthSessionOnline,
  removeExternalOauthSession,
  TRACKING_START_SIGNUP_SESSION_KEY
} from '@/router/router-utils';
import {
  getBigcommerceAccount,
  getShopifyAccount,
  getWixOauthId,
  insideProviderIframe
} from '@/utils/providerIframe';
import SignUpService from '@/api/SingupService';

import logger from '@/utils/logger';
import { OnboardingURL } from '@/views/Onboarding/helpers';
import { EBusinessOnboardingInitialFlow } from '@/models/business-onboarding';
import { loadCategoriesStore } from '@/utils/categories';

@Component({
  components: {
    BaseLayout,
    BaseLayoutAI
  }
})
export default class BusinessOnboarding extends Vue {
  signUpservice = new SignUpService();
  unsub: () => void = () => {};

  defaultAIFlowProps = {
    trackingSignup: false,
    BaseLayout: 'BaseLayoutAI',
    animated: false
  };

  get wrapperStyles() {
    const config = this.configByRouteName[this.$route.name as string];

    return {
      backgroundColor: config ? config.mainBg : '#fff'
    };
  }

  get BaseLayoutComponent() {
    const config = this.configByRouteName[this.$route.name as string];

    if (config.BaseLayout) return config.BaseLayout;

    return 'BaseLayout';
  }

  get routeName(): any {
    return this.$route.name;
  }

  get yourStoreDetailsFormData() {
    return this.$store.getters['businessOnboarding/getStoreDetailsForm'];
  }

  get animationEnabled() {
    const { animated = true } =
      this.configByRouteName[this.$route.name as string] || {};

    if (animated && window.previousRouteName) {
      const { animated: oldAnimated = true } =
        this.configByRouteName[window.previousRouteName as string] || {};

      return oldAnimated;
    }

    return animated;
  }

  get configByRouteName(): Record<string, any> {
    return {
      'bo-your-account': {},
      'bo-your-store': {},
      'bo-your-store-operation': {},
      'bo-your-store-details': {
        showNavProgress: false,
        routeBackButton: OnboardingURL.YOUR_BUSINESS_PREVIEW,
        animated: false
      },
      'bo-your-store-complete': {
        animated: true
      },
      'bo-your-store-address': {},
      'bo-your-store-preview': {},
      'bo-your-store-providers': {},
      'bo-welcome-twism': {
        showNavProgress: false,
        trackingSignup: false,
        animated: false
      },

      // INVITATION FLOW
      'bo-invitation-create-account-owner': {
        showNavProgress: false,
        trackingSignup: false
      },
      'bo-invitation-login-account-owner': {
        showNavProgress: false,
        trackingSignup: false
      },

      // HUBSPOT INTEGRATION FLOW
      'bo-invitation-merchant-create-account': {
        showNavProgress: false,
        trackingSignup: false,
        hideHeader: true,
        mainBg: '#F1F1F1'
      },
      'bo-invitation-merchant-create-account-login': {
        showNavProgress: false,
        trackingSignup: false,
        hideHeader: true,
        mainBg: '#F1F1F1'
      },

      // AI SIGNUP FLOW
      'bo-ai-business-preview': {
        ...this.defaultAIFlowProps
      },
      'bo-ai-business-edit': {
        ...this.defaultAIFlowProps
      },
      'bo-ai-business-complete': {
        ...this.defaultAIFlowProps
      },
      'bo-ai-loading': {
        ...this.defaultAIFlowProps,
        showBusinessName: true
      },
      'bo-ai-campaigns': {
        ...this.defaultAIFlowProps,
        showBusinessName: true
      },
      'bo-ai-your-account': {
        ...this.defaultAIFlowProps,
        showBusinessName: true
      },
      'bo-ai-create-campaigns': {
        ...this.defaultAIFlowProps,
        hideHeader: true
      },
      'bo-ai-coin-network': {
        ...this.defaultAIFlowProps,
        showBusinessName: false,
        hideHeader: true
      }
    };
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  get flags() {
    return this.$store.getters['flags/getFlags'];
  }

  get isInsideProviderIframe() {
    return insideProviderIframe();
  }

  get externalOauthSessionOnline() {
    return isExternalOauthSessionOnline();
  }

  get providerName() {
    return this.$route.query.providerName;
  }

  async getRedirectUrlByProvider() {
    switch (this.providerName) {
      case 'shopify':
        return this.signUpservice.connectShopify(getShopifyAccount());
      case 'bigcommerce':
        return this.signUpservice.connectBigcommerce(getBigcommerceAccount());
      case 'wix':
        return this.signUpservice.connectWix(getWixOauthId());
      default:
        return { data: { url: '' } };
    }
  }

  loadCategories = async () => {
    return loadCategoriesStore();
  };

  async mounted() {
    if (this.businessInfo || segmentIo.getIdentifiedId()) {
      segmentIo.reset();
    }
    logger.reset();

    const { trackingSignup = true } =
      this.configByRouteName[this.$route.name as string] || {};

    if (
      !localStorage.getItem(TRACKING_START_SIGNUP_SESSION_KEY) &&
      trackingSignup
    ) {
      segmentIo.track('BFR Sign Up | Started Merchant Signup', {
        'Is First Business': !this.businessInfo,
        'Direct Link': !isExternalOauthSessionOnline()
      });
      localStorage.setItem(
        TRACKING_START_SIGNUP_SESSION_KEY,
        EBusinessOnboardingInitialFlow.DIRECT_LINK
      );
    }

    await this.loadCategories();

    this.unsub = this.$store.subscribe((mutation, state) => {
      if (
        !mutation.type.startsWith('businessOnboarding/') ||
        this.$route.name === 'bo-your-digital-coin'
      )
        return;

      setBusinessOnboardingDataOnLocalStorage(state.businessOnboarding);
    });

    this.resizeEventHandler();

    window.addEventListener('resize', this.resizeEventHandler);
  }

  beforeDestroy() {
    if (this.businessInfo) {
      logger.identifyBusiness(this.businessInfo.id);
      segmentIo.identifyBusiness(this.businessInfo);
      removeExternalOauthSession();
      localStorage.removeItem(TRACKING_START_SIGNUP_SESSION_KEY);
    }

    this.unsub();

    window.removeEventListener('resize', this.resizeEventHandler);
  }

  resizeEventHandler() {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty('--onboarding-vh', `${vh}px`);
  }
}
